import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import {AppState} from 'app/store';
import {fetchAircraftById, clearSelectedAircraft} from '../store/aircrafts/aircrafts.slice';
import PageHeader from 'app/main/shared-components/page-header/PageHeader';
import {
  CircularProgress, Tooltip, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Box,
} from '@material-ui/core';
import {aircraftDetailStyles} from './aircrafts.styles';
import history from '@history';
import {AircraftService, SeatClassService} from 'app/services';
import {SeatClassResponse} from 'app/types/service-types/AircraftTypes';
import {successMsg, errorMsg} from 'app/utils/MessageUtils';

/** Color palette with multiple auto-assigned class colors */
const CLASS_PALETTE = [
  {bg: '#fff8e1', color: '#e65100', border: '#ffe082'},   // Amber / Business
  {bg: '#e3f2fd', color: '#1565c0', border: '#90caf9'},   // Blue / Economy Plus
  {bg: '#f1f8e9', color: '#33691e', border: '#aed581'},   // Green / Economy
  {bg: '#fce4ec', color: '#ad1457', border: '#f48fb1'},   // Pink
  {bg: '#ede7f6', color: '#4527a0', border: '#b39ddb'},   // Purple
  {bg: '#e0f7fa', color: '#00695c', border: '#80deea'},   // Cyan
];

const AircraftDetailPage = () => {
  const classes = aircraftDetailStyles();
  const dispatch = useDispatch();
  const {id} = useParams<{id: string}>();

  const {selectedAircraft, detailLoading} = useSelector(
    (state: AppState) => state.configurations.aircrafts.aircrafts
  );

  useEffect(() => {
    if (id) {
      dispatch(fetchAircraftById(id));
    }
    return () => {
      dispatch(clearSelectedAircraft());
    };
  }, [id]);

  /* ---- Seat class assignment dialog state ---- */
  const [assignOpen, setAssignOpen] = useState(false);
  const [seatClasses, setSeatClasses] = useState<SeatClassResponse[]>([]);
  const [assignForm, setAssignForm] = useState({seatClassId: '', fromRow: '', toRow: ''});
  const [assignLoading, setAssignLoading] = useState(false);

  useEffect(() => {
    SeatClassService.getAll('ACTIVE').then(data => {
      const classList = 'content' in data ? data.content : data;
      setSeatClasses(classList);
    }).catch(() => {});
  }, []);

  const handleAssignSeatClass = async () => {
    if (!assignForm.seatClassId || !assignForm.fromRow || !assignForm.toRow) return;
    setAssignLoading(true);
    try {
      await AircraftService.assignSeatClass(
        id,
        assignForm.seatClassId,
        Number(assignForm.fromRow),
        Number(assignForm.toRow)
      );
      dispatch(successMsg('Seat class assigned successfully'));
      setAssignOpen(false);
      setAssignForm({seatClassId: '', fromRow: '', toRow: ''});
      dispatch(fetchAircraftById(id)); // Refresh
    } catch (err: any) {
      dispatch(errorMsg(err?.response?.data?.message || 'Failed to assign seat class'));
    }
    setAssignLoading(false);
  };

  /* ---- Derived data ---- */
  const columns = useMemo(() => {
    if (!selectedAircraft) return [];
    const raw = selectedAircraft.seatColumns || '';
    // Handle both "A,B,C" and "ABC" formats
    return raw.includes(',') ? raw.split(',').map((c: string) => c.trim()) : raw.split('');
  }, [selectedAircraft]);

  const seatsByRow = useMemo(() => {
    const map: Record<number, Record<string, any>> = {};
    selectedAircraft?.seatDefinitions?.forEach(seat => {
      if (!map[seat.rowNumber]) map[seat.rowNumber] = {};
      map[seat.rowNumber][seat.columnLetter] = seat;
    });
    return map;
  }, [selectedAircraft]);

  // Build color map per class name (stable order by priority)
  const classColorMap = useMemo(() => {
    const m = new Map<string, typeof CLASS_PALETTE[0]>();
    if (!selectedAircraft) return m;
    const seen = new Map<string, number>();
    selectedAircraft.seatDefinitions?.forEach(s => {
      if (s.seatClass && !seen.has(s.seatClass.name)) {
        seen.set(s.seatClass.name, s.seatClass.priority ?? 999);
      }
    });
    const sorted = [...seen.entries()].sort((a, b) => a[1] - b[1]);
    sorted.forEach(([name], i) => m.set(name, CLASS_PALETTE[i % CLASS_PALETTE.length]));
    return m;
  }, [selectedAircraft]);

  // Detect exit & over-wing rows from seat properties
  const exitRows = useMemo(() => {
    const set = new Set<number>();
    selectedAircraft?.seatDefinitions?.forEach(s => { if (s.nearExit) set.add(s.rowNumber); });
    return set;
  }, [selectedAircraft]);

  const overWingRows = useMemo(() => {
    const set = new Set<number>();
    selectedAircraft?.seatDefinitions?.forEach(s => { if (s.overWing) set.add(s.rowNumber); });
    return set;
  }, [selectedAircraft]);

  // Detect aisle position (gap between left group and right group)
  const aisleAfterIndex = useMemo(() => {
    if (columns.length <= 3) return -1; // no aisle for tiny configs
    return Math.floor(columns.length / 2) - 1; // e.g. ABCDEF -> gap after C (index 2)
  }, [columns]);

  /* ---- Detect class boundaries for visual dividers ---- */
  const classBoundaries = useMemo(() => {
    const set = new Set<number>();
    if (!selectedAircraft) return set;
    const totalRows = selectedAircraft.totalRows;
    for (let r = 1; r < totalRows; r++) {
      const currSeat = seatsByRow[r]?.[columns[0]];
      const nextSeat = seatsByRow[r + 1]?.[columns[0]];
      if (currSeat && nextSeat && currSeat.seatClass?.id !== nextSeat.seatClass?.id) {
        set.add(r);
      }
    }
    return set;
  }, [selectedAircraft, seatsByRow, columns]);

  /* ---- Loading / empty states ---- */
  if (detailLoading) {
    return (
      <div style={{display: 'flex', justifyContent: 'center', padding: 40}}>
        <CircularProgress/>
      </div>
    );
  }

  if (!selectedAircraft) {
    return (
      <div style={{padding: 24, textAlign: 'center', color: '#999'}}>
        Aircraft not found
      </div>
    );
  }

  /* ---- Render helpers ---- */
  const getSeatColor = (seat: any) => {
    if (seat.blocked) return {bg: '#eceff1', color: '#b0bec5', border: '#cfd8dc'};
    return classColorMap.get(seat.seatClass?.name || '') || {bg: '#f5f5f5', color: '#616161', border: '#e0e0e0'};
  };

  const buildTooltip = (seat: any) => {
    const parts: string[] = [];
    if (seat.nearWindow) parts.push('Window');
    if (seat.nearAisle) parts.push('Aisle');
    if (seat.nearExit) parts.push('Exit Row');
    if (seat.nearLavatory) parts.push('Near Lavatory');
    if (seat.overWing) parts.push('Over Wing');
    if (seat.blocked) parts.push('BLOCKED');
    return (
      <div style={{fontSize: 12, lineHeight: 1.5}}>
        <div style={{fontWeight: 700, fontSize: 13}}>{seat.label}</div>
        <div>{seat.seatClass?.name || 'Unassigned'}</div>
        {seat.seatClass?.basePrice != null && <div>${seat.seatClass.basePrice.toFixed(2)}</div>}
        {parts.length > 0 && <div style={{color: '#ffcc80', marginTop: 2}}>{parts.join(' · ')}</div>}
      </div>
    );
  };

  const renderSeat = (rowNum: number, col: string) => {
    const seat = seatsByRow[rowNum]?.[col];
    if (!seat) return <div key={col} style={{width: 34, height: 34, margin: '0 2px'}}/>;
    const colors = getSeatColor(seat);
    return (
      <Tooltip key={col} title={buildTooltip(seat)} arrow placement="top">
        <div
          className={`${classes.seat} ${seat.blocked ? classes.seatBlocked : ''}`}
          style={!seat.blocked ? {backgroundColor: colors.bg, color: colors.color, borderColor: colors.border} : undefined}
        >
          {seat.blocked ? '✕' : seat.label}
        </div>
      </Tooltip>
    );
  };

  return (
    <div className={classes.root}>
      <PageHeader
        title={`${selectedAircraft.name} (${selectedAircraft.code})`}
        desc="Aircraft configuration and seat layout"
        backButton={() => history.goBack()}
      />

      {/* Info Card */}
      <div className={classes.detailCard}>
        <div className={classes.infoGrid}>
          <div className={classes.infoItem}><label>Code</label><span>{selectedAircraft.code}</span></div>
          <div className={classes.infoItem}><label>Name</label><span>{selectedAircraft.name}</span></div>
          <div className={classes.infoItem}><label>Total Rows</label><span>{selectedAircraft.totalRows}</span></div>
          <div className={classes.infoItem}><label>Columns</label><span>{columns.join('')}</span></div>
          <div className={classes.infoItem}><label>Total Seats</label><span>{selectedAircraft.totalSeats}</span></div>
          <div className={classes.infoItem}><label>Layout</label><span>{selectedAircraft.layoutDescription || '-'}</span></div>
        </div>
        <Box mt={1}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setAssignOpen(true)}
          >
            Assign Seat Class to Rows
          </Button>
        </Box>
      </div>

      {/* Legend */}
      <div className={classes.detailCard}>
        <div style={{fontWeight: 600, marginBottom: 10, color: '#333', fontSize: 14}}>Seat Classes</div>
        <div className={classes.legendContainer}>
          {Array.from(classColorMap.entries()).map(([name, color]) => (
            <div key={name} className={classes.legendItem}>
              <div className={classes.legendSwatch} style={{backgroundColor: color.bg, borderColor: color.border}}/>
              {name}
            </div>
          ))}
          <div className={classes.legendItem}>
            <div className={classes.legendSwatch} style={{backgroundColor: '#eceff1', borderColor: '#cfd8dc'}}/>
            Blocked
          </div>
        </div>
      </div>

      {/* ============ SEAT MAP — Airplane Shape ============ */}
      <div className={classes.detailCard}>
        <div style={{fontWeight: 600, marginBottom: 12, color: '#333', fontSize: 14}}>Seat Map</div>

        <div className={classes.fuselageWrapper}>
          <div className={classes.fuselage}>
            {/* Cockpit Nose */}
            <div className={classes.noseOverlay}>
              <span className={classes.cockpitIcon}>✈</span>
            </div>

            {/* Column Header Row */}
            <div className={classes.colHeaderRow}>
              <div className={classes.rowLabel}/>
              {columns.map((col: string, ci: number) => (
                <React.Fragment key={col}>
                  {ci === aisleAfterIndex + 1 && ci > 0 && <div className={classes.aisleGap}/>}
                  <div style={{width: 34, margin: '0 2px'}} className={classes.colHeaderLabel}>{col}</div>
                </React.Fragment>
              ))}
              <div className={classes.rowLabel}/>
            </div>

            {/* Seat Rows */}
            {Array.from({length: selectedAircraft.totalRows}, (_, i) => i + 1).map(rowNum => {
              const isExitRow = exitRows.has(rowNum);
              const isOverWing = overWingRows.has(rowNum);
              const showDivider = classBoundaries.has(rowNum);
              return (
                <React.Fragment key={rowNum}>
                  <div className={classes.seatRow} style={isOverWing ? {background: 'rgba(144,164,174,0.07)', borderRadius: 4} : undefined}>
                    {/* Left row number */}
                    <div className={classes.rowLabel}>{rowNum}</div>

                    {/* Seats with aisle gap */}
                    {columns.map((col: string, ci: number) => (
                      <React.Fragment key={col}>
                        {ci === aisleAfterIndex + 1 && ci > 0 && <div className={classes.aisleGap}/>}
                        {renderSeat(rowNum, col)}
                      </React.Fragment>
                    ))}

                    {/* Right row number */}
                    <div className={classes.rowLabel}>{rowNum}</div>

                    {/* Exit row indicators */}
                    {isExitRow && (
                      <>
                        <span className={`${classes.exitLabel} ${classes.exitLeft}`}>EXIT</span>
                        <span className={`${classes.exitLabel} ${classes.exitRight}`}>EXIT</span>
                      </>
                    )}

                    {/* Over-wing indicators */}
                    {isOverWing && overWingRows.has(rowNum) && !overWingRows.has(rowNum - 1) && (
                      <>
                        <span className={`${classes.wingIndicator} ${classes.wingLeft}`}>▸</span>
                        <span className={`${classes.wingIndicator} ${classes.wingRight}`}>◂</span>
                      </>
                    )}
                  </div>
                  {showDivider && <div className={classes.classDivider}/>}
                </React.Fragment>
              );
            })}

            {/* Tail */}
            <div className={classes.tailSection}>aft</div>
          </div>
        </div>
      </div>

      {/* ===== Assign Seat Class Dialog ===== */}
      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Seat Class to Row Range</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gridGap={16}>
            <TextField
              select
              label="Seat Class"
              variant="outlined"
              size="small"
              value={assignForm.seatClassId}
              onChange={e => setAssignForm(p => ({...p, seatClassId: e.target.value}))}
              fullWidth
            >
              {seatClasses.map(sc => (
                <MenuItem key={sc.id} value={sc.id}>
                  {sc.name} — ${sc.basePrice}
                </MenuItem>
              ))}
            </TextField>
            <Box display="flex" gridGap={12}>
              <TextField
                label="From Row"
                variant="outlined"
                size="small"
                type="number"
                value={assignForm.fromRow}
                onChange={e => setAssignForm(p => ({...p, fromRow: e.target.value}))}
                fullWidth
                InputProps={{inputProps: {min: 1, max: selectedAircraft.totalRows}}}
              />
              <TextField
                label="To Row"
                variant="outlined"
                size="small"
                type="number"
                value={assignForm.toRow}
                onChange={e => setAssignForm(p => ({...p, toRow: e.target.value}))}
                fullWidth
                InputProps={{inputProps: {min: 1, max: selectedAircraft.totalRows}}}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)} disabled={assignLoading}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAssignSeatClass}
            disabled={assignLoading || !assignForm.seatClassId || !assignForm.fromRow || !assignForm.toRow}
          >
            {assignLoading ? 'Assigning...' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AircraftDetailPage;
